/**
 * useMessaging Hook
 * Phase 6: Communication & Engagement
 * React hooks for messaging functionality with real-time updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { messagingService } from '../services/messagingService';

/**
 * Hook for managing conversations list
 * @param {string} userId - User's seller or buyer ID
 * @param {string} userType - 'seller' or 'buyer'
 */
export function useConversations(userId, userType) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const subscriptionRef = useRef(null);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!userId || !userType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await messagingService.getConversations(userId, userType);
      setConversations(data);

      // Calculate unread count
      const unreadColumn = userType === 'seller' ? 'unread_for_seller' : 'unread_for_buyer';
      const total = data.reduce((sum, conv) => sum + (conv[unreadColumn] || 0), 0);
      setUnreadCount(total);

      setError(null);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, userType]);

  // Subscribe to real-time updates with polling fallback
  useEffect(() => {
    if (!userId || !userType) return;

    loadConversations();

    // Subscribe to conversation updates
    subscriptionRef.current = messagingService.subscribeToConversations(
      userId,
      userType,
      (payload) => {
        // Reload conversations on any change
        loadConversations();
      }
    );

    // Polling fallback: refresh every 30 seconds for stability
    // This ensures unread count stays accurate even if real-time events are missed
    const pollInterval = setInterval(() => {
      loadConversations();
    }, 30000);

    return () => {
      if (subscriptionRef.current) {
        messagingService.unsubscribe(subscriptionRef.current);
      }
      clearInterval(pollInterval);
    };
  }, [userId, userType, loadConversations]);

  // Archive conversation
  const archiveConversation = async (conversationId) => {
    try {
      await messagingService.archiveConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
    } catch (err) {
      console.error('Error archiving conversation:', err);
      throw err;
    }
  };

  return {
    conversations,
    loading,
    error,
    unreadCount,
    refresh: loadConversations,
    archiveConversation
  };
}

/**
 * Hook for managing a single conversation with messages
 * @param {string} conversationId - Conversation UUID
 * @param {string} userType - 'seller' or 'buyer' (for marking read)
 */
export function useConversation(conversationId, userType) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);

  // Load conversation and messages
  const loadData = useCallback(async () => {
    if (!conversationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [convData, msgData] = await Promise.all([
        messagingService.getConversation(conversationId),
        messagingService.getMessages(conversationId)
      ]);

      setConversation(convData);
      setMessages(msgData);
      setError(null);

      // Mark messages as read
      if (userType) {
        await messagingService.markAsRead(conversationId, userType);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [conversationId, userType]);

  // Subscribe to new messages
  useEffect(() => {
    if (!conversationId) return;

    loadData();

    // Subscribe to new messages
    subscriptionRef.current = messagingService.subscribeToConversation(
      conversationId,
      (newMessage) => {
        // Add message only if it doesn't already exist (avoid duplicates)
        setMessages(prev => {
          if (prev.some(m => m.id === newMessage.id)) {
            return prev;
          }
          return [...prev, newMessage];
        });

        // Mark as read if from the other party
        if (userType && newMessage.sender_type !== userType) {
          messagingService.markAsRead(conversationId, userType);
        }
      }
    );

    return () => {
      if (subscriptionRef.current) {
        messagingService.unsubscribe(subscriptionRef.current);
      }
    };
  }, [conversationId, userType, loadData]);

  // Send message
  const sendMessage = async ({ subject, body, attachments = [] }) => {
    if (!conversation) return;

    try {
      setSending(true);
      const senderId = userType === 'seller' ? conversation.seller_id : conversation.buyer_id;

      const newMessage = await messagingService.sendMessage({
        conversationId,
        senderId,
        senderType: userType,
        subject,
        body,
        attachments
      });

      // Add message to local state immediately for instant feedback
      if (newMessage && newMessage.id) {
        setMessages(prev => {
          // Check if message already exists (from real-time subscription)
          if (prev.some(m => m.id === newMessage.id)) {
            return prev;
          }
          return [...prev, newMessage];
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  };

  return {
    conversation,
    messages,
    loading,
    sending,
    error,
    sendMessage,
    refresh: loadData
  };
}

/**
 * Hook for checking messaging permissions
 * @param {string} sellerId - Seller UUID
 * @param {string} buyerId - Buyer UUID
 */
export function useMessagePermission(sellerId, buyerId) {
  const [permission, setPermission] = useState({ allowed: true, reason: '', matchScore: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkPermission() {
      if (!sellerId || !buyerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await messagingService.canSendMessage(sellerId, buyerId);
        setPermission(result);
      } catch (err) {
        console.error('Error checking permission:', err);
        // Default to allowed on error
        setPermission({ allowed: true, reason: 'Permission check unavailable', matchScore: 0 });
      } finally {
        setLoading(false);
      }
    }

    checkPermission();
  }, [sellerId, buyerId]);

  return { ...permission, loading };
}

/**
 * Hook for message templates
 * @param {string} [category] - Optional category filter
 */
export function useMessageTemplates(category = null) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadTemplates() {
      try {
        setLoading(true);
        const data = await messagingService.getTemplates(category);
        setTemplates(data);
        setError(null);
      } catch (err) {
        console.error('Error loading templates:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadTemplates();
  }, [category]);

  // Fill a template with data
  const fillTemplate = async (templateId, data) => {
    try {
      return await messagingService.fillTemplate(templateId, data);
    } catch (err) {
      console.error('Error filling template:', err);
      throw err;
    }
  };

  return {
    templates,
    loading,
    error,
    fillTemplate
  };
}

/**
 * Hook for managing buyer's messaging permissions/settings
 * @param {string} buyerId - Buyer UUID
 */
export function useMessagingSettings(buyerId) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load settings
  useEffect(() => {
    async function loadSettings() {
      if (!buyerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await messagingService.getPermissions(buyerId);
        setSettings(data);
        setError(null);
      } catch (err) {
        console.error('Error loading settings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, [buyerId]);

  // Update settings
  const updateSettings = async (newSettings) => {
    if (!buyerId) return;

    try {
      setSaving(true);
      await messagingService.updatePermissions(buyerId, newSettings);
      setSettings(prev => ({ ...prev, ...newSettings }));
    } catch (err) {
      console.error('Error updating settings:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Block/unblock seller
  const blockSeller = async (sellerId) => {
    try {
      setSaving(true);
      await messagingService.blockSeller(buyerId, sellerId);
      setSettings(prev => ({
        ...prev,
        blocked_sellers: [...(prev.blocked_sellers || []), sellerId]
      }));
    } catch (err) {
      console.error('Error blocking seller:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const unblockSeller = async (sellerId) => {
    try {
      setSaving(true);
      await messagingService.unblockSeller(buyerId, sellerId);
      setSettings(prev => ({
        ...prev,
        blocked_sellers: (prev.blocked_sellers || []).filter(id => id !== sellerId)
      }));
    } catch (err) {
      console.error('Error unblocking seller:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return {
    settings,
    loading,
    saving,
    error,
    updateSettings,
    blockSeller,
    unblockSeller
  };
}

/**
 * Hook for file uploads
 */
export function useFileUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const uploadFile = async (file, conversationId) => {
    try {
      setUploading(true);
      setProgress(0);
      setError(null);

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit');
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not allowed');
      }

      // Simulate progress (actual upload doesn't provide progress)
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const result = await messagingService.uploadAttachment(file, conversationId);

      clearInterval(progressInterval);
      setProgress(100);

      return result;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadFile,
    uploading,
    progress,
    error
  };
}

export default {
  useConversations,
  useConversation,
  useMessagePermission,
  useMessageTemplates,
  useMessagingSettings,
  useFileUpload
};
